/**
 * Procedural environment cube map, generated from the same palette as the dome and the
 * ocean.
 *
 * Without an environment texture, PBR surfaces get no ambient specular at all: metals go
 * black and painted steel reads as flat matte board. The cube includes deterministic
 * low-frequency cloud radiance rather than a flat gradient, so rough steel and the sea
 * receive broad moving-weather variation even before the planned HDRI pipeline lands.
 */

import { RawCubeTexture } from '@babylonjs/core/Materials/Textures/rawCubeTexture';
import { Constants } from '@babylonjs/core/Engines/constants';
import type { Scene } from '@babylonjs/core/scene';
import type { Environment } from './environment';
import { clamp01, lerp } from '../sim/math';

const FACE_SIZE = 64;

function smooth01(t: number): number {
  const k = clamp01(t);
  return k * k * (3 - 2 * k);
}

/** Stable scalar hash used only for render-side cloud radiance. */
function hash2(x: number, y: number): number {
  const h = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return h - Math.floor(h);
}

/** Continuous value noise; using world direction coordinates keeps cube edges seamless. */
function valueNoise2(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = smooth01(fx);
  const uy = smooth01(fy);

  const a = lerp(hash2(ix, iy), hash2(ix + 1, iy), ux);
  const b = lerp(hash2(ix, iy + 1), hash2(ix + 1, iy + 1), ux);
  return lerp(a, b, uy);
}

function fbm2(x: number, y: number): number {
  let value = 0;
  let amplitude = 0.54;
  let total = 0;
  let px = x;
  let py = y;
  for (let octave = 0; octave < 4; octave++) {
    value += valueNoise2(px, py) * amplitude;
    total += amplitude;
    // Rotate as scale increases so the octaves do not line up into obvious bands.
    const nextX = px * 1.62 + py * 1.17 + 13.7;
    py = -px * 1.11 + py * 1.69 + 7.3;
    px = nextX;
    amplitude *= 0.51;
  }
  return value / total;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  return smooth01((x - edge0) / (edge1 - edge0));
}

function mixComponent(a: number, b: number, t: number): number {
  return a + (b - a) * clamp01(t);
}

/**
 * Low-frequency counterpart to the visible 2.5D cloud decks. This is deliberately not
 * pixel-identical to the dome: the environment cube supplies broad radiance for PBR and
 * water reflections, where fine cloud detail would alias and add no useful lighting.
 */
function cloudRadianceAt(
  env: Environment,
  x: number,
  y: number,
  z: number,
): [number, number, number, number] {
  if (y <= -0.02 || env.cloudCover <= 0.001) return [0, 0, 0, 0];

  const planeScale = 1 / Math.max(y + 0.26, 0.055);
  const planeX = x * planeScale;
  const planeZ = z * planeScale;
  const crossX = -env.cloudWindZ;
  const crossZ = env.cloudWindX;
  const across = planeX * crossX + planeZ * crossZ;
  const along = planeX * env.cloudWindX + planeZ * env.cloudWindZ;

  const warpX = fbm2(across * 0.18 + 2.4, along * 0.22 + 5.1) - 0.5;
  const warpY = fbm2(across * 0.16 + 8.7, along * 0.19 + 1.3) - 0.5;
  const mass = fbm2(across * 0.38 + warpX * 0.72, along * 0.46 + warpY * 0.72);
  const cells = fbm2(across * 0.91 + warpY * 0.43 + 4.9, along * 1.14 + warpX * 0.43);
  const rolls = fbm2(across * 0.24 + 3.1, along * 1.52 + 9.4);
  const rawField = mass * 0.58 + cells * 0.30 + rolls * 0.12;
  const field = clamp01((rawField - 0.25) * 1.82);

  const threshold = lerp(0.64, 0.40, env.cloudCover);
  const transition = lerp(0.145, 0.115, env.cloudCover);
  let density = smoothstep(threshold - transition, threshold + transition, field);
  const overcast = smoothstep(0.72, 1, env.cloudCover);
  density = lerp(density, 0.13 + density * 0.87, overcast);

  const relief = clamp01(
    (field - 0.30) * 1.35
      + (cells - 0.5) * 0.86
      + (rolls - 0.5) * 0.74,
  );

  const lightAcross = (
    env.sunDir.x * crossX + env.sunDir.z * crossZ
  ) / Math.max(env.sunDir.y + 0.18, 0.25);
  const lightAlong = (
    env.sunDir.x * env.cloudWindX + env.sunDir.z * env.cloudWindZ
  ) / Math.max(env.sunDir.y + 0.18, 0.25);
  const shadowField = fbm2(
    (across + lightAcross * 0.11) * 0.38 + 6.8,
    (along + lightAlong * 0.11) * 0.46 + 2.9,
  );
  const shadowDensity = smoothstep(
    threshold - transition,
    threshold + transition,
    clamp01((shadowField - 0.25) * 1.82),
  );
  const transmission = Math.exp(
    -(shadowDensity * 0.82 + density * 0.34)
      * lerp(0.95, 2.35, env.storminess),
  );

  const shadowScale = lerp(0.25, 0.075, env.storminess);
  const middleScale = lerp(0.82, 0.46, env.storminess);
  const lightScale = lerp(1.08, 0.72, env.storminess);
  const shadowR = mixComponent(env.zenithColor.r, env.fogColor.r, 0.19) * shadowScale;
  const shadowG = mixComponent(env.zenithColor.g, env.fogColor.g, 0.19) * shadowScale;
  const shadowB = mixComponent(env.zenithColor.b, env.fogColor.b, 0.19) * shadowScale;
  const middleR = mixComponent(env.zenithColor.r, env.fogColor.r, 0.61) * middleScale;
  const middleG = mixComponent(env.zenithColor.g, env.fogColor.g, 0.61) * middleScale;
  const middleB = mixComponent(env.zenithColor.b, env.fogColor.b, 0.61) * middleScale;
  const lightR = mixComponent(env.fogColor.r, env.sunColor.r, 0.12) * lightScale;
  const lightG = mixComponent(env.fogColor.g, env.sunColor.g, 0.12) * lightScale;
  const lightB = mixComponent(env.fogColor.b, env.sunColor.b, 0.12) * lightScale;

  const bodyLight = relief
    * (0.28 + transmission * 0.72)
    * lerp(0.94, 0.48, smoothstep(0.36, 0.86, density));
  const middleBlend = smoothstep(0.16, 0.76, bodyLight);
  const edge = smoothstep(0.035, 0.34, density)
    * (1 - smoothstep(0.43, 0.82, density));
  const lightBlend = edge * transmission * lerp(0.34, 0.15, env.storminess);
  let r = mixComponent(shadowR, middleR, middleBlend);
  let g = mixComponent(shadowG, middleG, middleBlend);
  let b = mixComponent(shadowB, middleB, middleBlend);
  r = mixComponent(r, lightR, lightBlend);
  g = mixComponent(g, lightG, lightBlend);
  b = mixComponent(b, lightB, lightBlend);
  const reliefScale = lerp(0.73, 1.10, smoothstep(0.08, 0.92, relief));
  r *= reliefScale;
  g *= reliefScale;
  b *= reliefScale;

  const ceiling = smoothstep(0.04, 0.72, Math.max(y, 0));
  const ceilingScale = lerp(
    1,
    lerp(0.76, 0.48, env.storminess),
    ceiling * env.cloudCover,
  );
  r *= ceilingScale;
  g *= ceilingScale;
  b *= ceilingScale;

  const patternVisibility = smoothstep(-0.012, 0.105, y);
  const coverPresence = lerp(0.56, 0.98, smoothstep(0, 0.82, env.cloudCover));
  const opacity = density * coverPresence * lerp(0.30, 0.98, patternVisibility);
  return [r, g, b, opacity];
}

/** TypeScript mirror of skyColor() in skyShared.glsl. Must track it. */
function skyColorAt(env: Environment, dx: number, dy: number, dz: number): [number, number, number] {
  const len = Math.hypot(dx, dy, dz) || 1;
  const x = dx / len;
  const y = dy / len;
  const z = dz / len;

  const t = Math.pow(1 - clamp01(y), env.horizonFalloff);
  let r = lerp(env.zenithColor.r, env.horizonColor.r, t);
  let g = lerp(env.zenithColor.g, env.horizonColor.g, t);
  let b = lerp(env.zenithColor.b, env.horizonColor.b, t);

  const sunDot = Math.max(0, x * env.sunDir.x + y * env.sunDir.y + z * env.sunDir.z);
  const bloom = Math.pow(sunDot, env.sunGlowPower) * env.sunGlowIntensity;
  r += env.sunColor.r * bloom;
  g += env.sunColor.g * bloom;
  b += env.sunColor.b * bloom;

  if (y >= -0.02) {
    const [cloudR, cloudG, cloudB, cloudOpacity] = cloudRadianceAt(env, x, y, z);
    r = lerp(r, cloudR, cloudOpacity);
    g = lerp(g, cloudG, cloudOpacity);
    b = lerp(b, cloudB, cloudOpacity);
  }

  // Below the horizon the environment is the sea, not sky — dark and desaturated. This
  // is what gives the rig's underside a plausible dark bounce instead of glowing.
  if (y < 0) {
    const k = clamp01(-y * 2.2);
    r = lerp(r, env.deepColor.r * 2.5 + 0.02, k);
    g = lerp(g, env.deepColor.g * 2.5 + 0.025, k);
    b = lerp(b, env.deepColor.b * 2.5 + 0.03, k);
  }
  return [r, g, b];
}

/** Direction for texel (u,v) on cube face `face`, using the standard cube-map layout. */
function faceDirection(face: number, u: number, v: number): [number, number, number] {
  // u,v in [-1, 1]
  switch (face) {
    case 0: return [1, -v, -u];   // +X
    case 1: return [-1, -v, u];   // -X
    case 2: return [u, 1, v];     // +Y
    case 3: return [u, -1, -v];   // -Y
    case 4: return [u, -v, 1];    // +Z
    default: return [-u, -v, -1]; // -Z
  }
}

export class SkyEnvironment {
  private texture: RawCubeTexture | null = null;
  private readonly faces: Uint8Array[] = [];

  constructor(private readonly scene: Scene) {
    for (let i = 0; i < 6; i++) this.faces.push(new Uint8Array(FACE_SIZE * FACE_SIZE * 4));
  }

  /** Regenerate from the current palette and install as the scene environment. */
  update(env: Environment): void {
    const exposure = env.exposure;
    for (let face = 0; face < 6; face++) {
      const data = this.faces[face] as Uint8Array;
      for (let y = 0; y < FACE_SIZE; y++) {
        const v = (y + 0.5) / FACE_SIZE * 2 - 1;
        for (let x = 0; x < FACE_SIZE; x++) {
          const u = (x + 0.5) / FACE_SIZE * 2 - 1;
          const [dx, dy, dz] = faceDirection(face, u, v);
          const [r, g, b] = skyColorAt(env, dx, dy, dz);
          const o = (y * FACE_SIZE + x) * 4;
          // Stored in linear space: Babylon's PBR pipeline expects a linear environment.
          data[o + 0] = Math.round(clamp01(r * exposure) * 255);
          data[o + 1] = Math.round(clamp01(g * exposure) * 255);
          data[o + 2] = Math.round(clamp01(b * exposure) * 255);
          data[o + 3] = 255;
        }
      }
    }

    if (!this.texture) {
      this.texture = new RawCubeTexture(
        this.scene,
        this.faces as unknown as ArrayBufferView[],
        FACE_SIZE,
        Constants.TEXTUREFORMAT_RGBA,
        Constants.TEXTURETYPE_UNSIGNED_BYTE,
        true,
        false,
        Constants.TEXTURE_TRILINEAR_SAMPLINGMODE,
      );
      this.texture.name = 'skyEnvironment';
      this.texture.gammaSpace = false;
      this.scene.environmentTexture = this.texture;
    } else {
      this.texture.update(
        this.faces as unknown as ArrayBufferView[],
        Constants.TEXTUREFORMAT_RGBA,
        Constants.TEXTURETYPE_UNSIGNED_BYTE,
        false,
      );
    }
    // Scene-wide ambient scale so surfaces track the weather's overall brightness.
    this.scene.environmentIntensity = lerp(0.35, 1.15, clamp01(env.ambientIntensity * 1.8));
  }

  dispose(): void {
    this.texture?.dispose();
    this.texture = null;
  }
}
